import { useEffect } from 'react';
import { Card, Form, Input, Button, Typography, App as AntApp } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRegisterMutation } from './hooks';
import { useAuthStore } from '@starling/core/auth/store';
import { ApiError } from '@starling/core/api/errors';

const { Title, Text } = Typography;

interface RegisterValues {
  tenant_name: string;
  admin_name: string;
  admin_email: string;
  admin_password: string;
}

export function RegisterPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const mutation = useRegisterMutation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/customers', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onFinish = (values: RegisterValues): void => {
    mutation.mutate(values, {
      onSuccess: () => {
        void message.success(t('auth.register.success'));
        navigate('/customers', { replace: true });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          void message.error(t('auth.register.errors.email_taken'));
        } else {
          void message.error(
            err instanceof Error ? err.message : t('auth.register.errors.generic'),
          );
        }
      },
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 480 }} variant="borderless">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('auth.register.title')}
          </Title>
          <Text type="secondary">{t('auth.register.subtitle')}</Text>
        </div>
        <Form<RegisterValues>
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          disabled={mutation.isPending}
        >
          <Form.Item
            label={t('auth.register.tenant_name')}
            name="tenant_name"
            rules={[{ required: true, min: 1, max: 200 }]}
          >
            <Input autoComplete="organization" />
          </Form.Item>
          <Form.Item
            label={t('auth.register.admin_name')}
            name="admin_name"
            rules={[{ required: true, min: 1, max: 200 }]}
          >
            <Input autoComplete="name" />
          </Form.Item>
          <Form.Item
            label={t('auth.register.admin_email')}
            name="admin_email"
            rules={[{ required: true, type: 'email' }]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item
            label={t('auth.register.admin_password')}
            name="admin_password"
            rules={[{ required: true, min: 8 }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={mutation.isPending}
              size="large"
            >
              {t('auth.register.submit')}
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Text type="secondary">{t('auth.register.have_account')} </Text>
          <Link to="/login">{t('auth.register.login_link')}</Link>
        </div>
      </Card>
    </div>
  );
}
